import React, { FunctionComponent, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { useMutation } from '@apollo/react-hooks';

import { signOut, useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { REVOKE_TOKEN } from 'components/UserMgmt/UserMgmt.queries';
import UserMgmt from 'components/UserMgmt';
import Loading from 'components/Loading';
import LogoutModal from 'components/LogoutModal';

import { Routes } from 'Routes';

const TIMEOUT = 14 * 60 * 1_000;
const COUNTDOWN = 60;

const App: FunctionComponent = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [logoutTimer, setLogoutTimer] = useState(COUNTDOWN);
  const { checkStorage, isAuthenticated, isLoading } = useUserMgmt();
  const [revokeToken] = useMutation(REVOKE_TOKEN, {
    onError: (e) => {
      console.error(e.message);
    },
  });

  const handleLogout = async () => {
    const { refreshToken } = checkStorage();
    await Promise.all([
      revokeToken({
        variables: {
          token: refreshToken,
          clientId: process.env.COGNITO_USER_POOL_WEB_CLIENT_ID,
        },
      }),
      signOut(),
    ]);
  };

  const handleOnActive = () => {};

  const handleOnIdle = () => {
    const { refreshToken } = checkStorage();
    if (refreshToken) {
      setModalOpen(true);
    }
  };

  const handleStayLoggedIn = () => {
    setModalOpen(false);
    setLogoutTimer(COUNTDOWN);
  };

  useIdleTimer({
    timeout: TIMEOUT,
    onActive: handleOnActive,
    onIdle: handleOnIdle,
    crossTab: {
      emitOnAllTabs: true,
    },
  });

  useEffect(() => {
    if (modalOpen) {
      setTimeout(
        () => setLogoutTimer((count) => (count > 0 ? count - 1 : count)),
        1000,
      );
    }
    if (logoutTimer === 0) handleLogout();
  }, [modalOpen, logoutTimer]);

  if (isLoading) return <Loading loading />;

  if (!isAuthenticated) return <UserMgmt />;

  const referrer = sessionStorage.getItem('referrer');
  if (referrer) {
    sessionStorage.removeItem('referrer');
  }

  return (
    <>
      <LogoutModal
        showModal={modalOpen}
        handleOnClose={handleStayLoggedIn}
        seconds={logoutTimer}
      />

      <Routes />
      {referrer && <Redirect to={referrer} />}
    </>
  );
};

export default App;
